import { availableOverviewViews } from '@/modules/zy-crm/utils/overview-navigation';
import { queryContacts } from '@/services/bff/contact.api';
import type { OverviewEntity } from '@/services/bff/crm-overview.api';
import { queryCustomers } from '@/services/bff/customer.api';
import { queryLeads } from '@/services/bff/lead.api';
import { queryOpportunities } from '@/services/bff/opportunity.api';

export type DuplicateEntity = OverviewEntity;
export type NameValues = { name?: unknown; companyName?: unknown };
export type DuplicateCandidate = {
  uuid: string;
  name: string;
  exact: boolean;
  matchedFields: string[];
  ownerName?: string;
  customerName?: string;
};
export type DuplicateResult = {
  items: DuplicateCandidate[];
  truncated: boolean;
  failed: boolean;
};
export const normalizeName = (value: unknown) =>
  String(value || '')
    .trim()
    .toLowerCase();
export function nameTerms(
  entity: DuplicateEntity,
  values: NameValues,
): string[] {
  return Array.from(
    new Set(
      [values.name, ...(entity === 'lead' ? [values.companyName] : [])]
        .map(normalizeName)
        .filter(Boolean),
    ),
  );
}
export function nameKey(entity: DuplicateEntity, values: NameValues): string {
  return JSON.stringify(nameTerms(entity, values));
}

export async function searchDuplicateNames(
  entity: DuplicateEntity,
  values: NameValues,
  access: Record<string, boolean>,
): Promise<DuplicateResult> {
  const terms = nameTerms(entity, values);
  if (!terms.length) return { items: [], truncated: false, failed: false };
  const views = availableOverviewViews(entity, access);
  if (!views.length) return { items: [], truncated: false, failed: true };
  const pages = await Promise.allSettled(
    views.flatMap((viewMode) =>
      terms.map((nameKeyword) => {
        const params = { viewMode, nameKeyword, page: 1, pageSize: 5 };
        switch (entity) {
          case 'customer':
            return queryCustomers(
              params as Parameters<typeof queryCustomers>[0],
            );
          case 'contact':
            return queryContacts(params as Parameters<typeof queryContacts>[0]);
          case 'lead':
            return queryLeads(params as Parameters<typeof queryLeads>[0]);
          case 'opportunity':
            return queryOpportunities(
              params as Parameters<typeof queryOpportunities>[0],
            );
          default:
            throw new Error('不支持的查重业务类型');
        }
      }),
    ),
  );
  const candidates = new Map<string, DuplicateCandidate>();
  let failed = false;
  let truncated = false;
  for (const page of pages) {
    if (page.status === 'rejected') {
      failed = true;
      continue;
    }
    const result = page.value;
    if (!Array.isArray(result?.list) || !Number.isFinite(result.total)) {
      failed = true;
      continue;
    }
    truncated ||= result.total > result.list.length;
    for (const item of result.list) {
      const record = item as unknown as Record<string, unknown>;
      if (!record.uuid) continue;
      const fields = [
        { label: entity === 'lead' ? '标题' : '名称', value: record.name },
        ...(entity === 'lead'
          ? [{ label: '公司名称', value: record.companyName }]
          : []),
        ...(entity === 'customer'
          ? [{ label: '企业全称', value: record.fullName }]
          : []),
      ].filter((field) =>
        terms.some((term) => normalizeName(field.value).includes(term)),
      );
      // Never present a generic keyword hit (remark, telephone, etc.) as a name match.
      if (!fields.length) continue;
      const uuid = String(record.uuid);
      candidates.set(uuid, {
        uuid,
        name: String(
          record.name || record.fullName || record.companyName || uuid,
        ),
        exact: fields.some((field) =>
          terms.includes(normalizeName(field.value)),
        ),
        matchedFields: fields.map((field) => field.label),
        ownerName: String(
          record.ownerUserName ||
            record.ownerName ||
            record.ownerMemberName ||
            '',
        ),
        customerName: String(
          record.customerName ||
            (entity === 'lead' ? record.companyName : '') ||
            '',
        ),
      });
    }
  }
  const items = [...candidates.values()].sort(
    (a, b) => Number(b.exact) - Number(a.exact) || a.name.localeCompare(b.name),
  );
  return {
    items: items.slice(0, 5),
    truncated: truncated || items.length > 5,
    failed,
  };
}
